/* QaCaseDetail — full-page QA review workspace.
 *
 * Same rationale as the Planner: QA spends 5–10 minutes reviewing scans,
 * the plan deliverable and the timeline before approving or sending back.
 * URL-addressable so refresh / share-with-admin / open-in-new-tab all work.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    ShieldCheck, ArrowLeft, ArrowUUpLeft, CheckCircle, ClipboardText, Cube,
    Image as ImageIcon, Stethoscope, User, Link as LinkIcon, VideoCamera,
    PencilSimple, Buildings, Timer,
} from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import PlanPanel from "@/components/case/PlanPanel";
import { ManufacturingSpecPanel } from "@/components/case/ManufacturingSpecPanel";
import AuditDialog, { AuditHistoryPanel } from "@/components/qa/AuditDialog";
import { CaseStatusBadge } from "@/components/shared/CaseStatusBadge";
import { CaseInfoChipRow } from "@/components/shared/CaseInfoChipRow";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";


const STATUS_PILL = "bg-violet-100 text-violet-800 border border-violet-200";

const ROW_STATUS_LABELS = {
    submitted_to_qa: { label: "Awaiting QA",        cls: "bg-violet-100 text-violet-800 border border-violet-200" },
    sent_for_approval: { label: "With doctor",      cls: "bg-cyan-100 text-cyan-800 border border-cyan-200" },
    ready_for_review: { label: "With doctor",       cls: "bg-cyan-100 text-cyan-800 border border-cyan-200" },
    revision_requested: { label: "Revision",        cls: "bg-amber-100 text-amber-800 border border-amber-200" },
    in_planning: { label: "Re-planning",            cls: "bg-blue-100 text-blue-800 border border-blue-200" },
    info_requested: { label: "Awaiting doctor info", cls: "bg-orange-100 text-orange-800 border border-orange-200" },
    approved: { label: "Approved",                  cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    in_production: { label: "In production",        cls: "bg-orange-100 text-orange-800 border border-orange-200" },
    shipped: { label: "Shipped",                    cls: "bg-orange-100 text-orange-800 border border-orange-200" },
    in_treatment: { label: "In treatment",          cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
    completed: { label: "Completed",                cls: "bg-zinc-100 text-zinc-700 border border-zinc-200" },
};

const SCAN_KINDS = ["stl", "scan_upper", "scan_lower", "scan_bite"];
const PHOTO_KINDS = ["intraoral", "intraoral_front", "intraoral_left", "intraoral_right", "intraoral_upper_occlusal", "intraoral_lower_occlusal", "extraoral_front", "extraoral_profile"];
const XRAY_KINDS = ["xray_opg", "xray_lateral_ceph"];
const SETUP_KINDS = ["setup", "setup_plan"];


/* iter98.99by — Round-trip timing chip. Scans the case timeline for
   key events and surfaces the round-trip duration:
     • Planner accepted in (submitted → planning_started)
     • Doctor confirmed in (sent_for_approval → approved)
   Renders only when at least one of the two durations is computable so
   the chip doesn't add noise to brand-new cases.

   Quiet trust signal — useful for clinic-side sales conversations
   ("our planners pick up cases in 18 minutes on average"). */
function formatDuration(ms) {
    if (ms == null || ms < 0) return null;
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    if (hrs < 24) return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
    const days = Math.floor(hrs / 24);
    const remH = hrs % 24;
    return remH ? `${days}d ${remH}h` : `${days}d`;
}
function eventAt(timeline, type) {
    if (!Array.isArray(timeline)) return null;
    const ev = timeline.find((e) => e.type === type);
    return ev ? new Date(ev.created_at).getTime() : null;
}
function RoundTripChip({ c }) {
    const tl = c?.timeline || [];
    const submittedAt = eventAt(tl, "submitted")
        || (c.created_at ? new Date(c.created_at).getTime() : null);
    const planningStartedAt = eventAt(tl, "planning_started");
    const sentForApprovalAt = eventAt(tl, "sent_for_approval");
    const approvedAt = eventAt(tl, "approved");

    const plannerLatency = (submittedAt && planningStartedAt)
        ? formatDuration(planningStartedAt - submittedAt) : null;
    const doctorLatency = (sentForApprovalAt && approvedAt)
        ? formatDuration(approvedAt - sentForApprovalAt) : null;

    if (!plannerLatency && !doctorLatency) return null;
    return (
        <span
            className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold tabular-nums"
            data-testid="qa-roundtrip-chip"
            title="Case round-trip: planner acceptance + doctor approval latency"
        >
            <Timer size={11} weight="bold" /> Round-trip
            {plannerLatency && <span>· planner accepted in {plannerLatency}</span>}
            {doctorLatency && <span>· doctor confirmed in {doctorLatency}</span>}
        </span>
    );
}

export default function QaCaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    // bump to force AuditHistoryPanel reload after a new audit lands
    const [auditRefreshKey, setAuditRefreshKey] = useState(0);
    /* iter98.99i — Premium "QA cleared · sent to doctor" confirmation
     * dialog. Replaces the thin sonner toast after approve. Primary CTA
     * returns the QA to the inbox. */
    const [approvedOpen, setApprovedOpen] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/cases/${id}`)
            .then((r) => setC(r.data))
            .catch(() => {
                toast.error("Case not found");
                navigate("/coe/qa");
            })
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading && !c) return <div className="text-muted-foreground text-sm py-12 text-center">Loading case…</div>;
    if (!c) return null;

    const meta = ROW_STATUS_LABELS[c.status] || { label: c.status?.replace(/_/g, " ") || "—", cls: STATUS_PILL };
    const files = c.files || [];
    const scans = files.filter((f) => SCAN_KINDS.includes(f.kind));
    const photos = files.filter((f) => PHOTO_KINDS.includes(f.kind));
    const xrays = files.filter((f) => XRAY_KINDS.includes(f.kind));
    const setupFiles = files.filter((f) => SETUP_KINDS.includes(f.kind));
    const planVideo = files.find((f) => f.kind === "plan_video");

    const backLink = () => {
        const params = new URLSearchParams(location.search);
        const fromBucket = params.get("from");
        return fromBucket ? `/coe/qa?bucket=${fromBucket}` : "/coe/qa";
    };

    const approve = async () => {
        setBusy(true);
        try {
            await api.put(`/qa/cases/${c.id}/approve`, { note: "" });
            /* iter98.99i — Premium CaseSubmittedDialog replaces the thin
             * toast. Navigate-on-dismiss is wired below so closing the
             * dialog also routes back to the QA inbox in one step. */
            setApprovedOpen(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to approve");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="qa-case-detail" className="space-y-5">
            {/* Top breadcrumb */}
            <Link to={backLink()} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="qa-back">
                <ArrowLeft size={12} /> Back to inbox
            </Link>

            {/* HERO — compact 2-row design matching the Doctor / Planner
                portal PatientHeader for universal consistency. Row 1:
                avatar · name + demographic · (right) doctor + clinic
                side-by-side. Row 2: status badge · case code · planner ·
                diagnosis · created date. Phone never rendered (PII for
                COE). */}
            <div
                className="bg-card border border-border rounded-2xl px-4 py-3 sm:px-5 sm:py-4"
                data-testid="qa-case-hero"
            >
                {/* Row 1 — identity */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {c.patient_photo_data_url ? (
                        <img
                            src={c.patient_photo_data_url}
                            alt={c.patient_name}
                            className="w-9 h-9 rounded-full object-cover shrink-0 border border-border bg-secondary"
                            data-testid="qa-patient-avatar-img"
                            loading="lazy"
                        />
                    ) : (
                        <div
                            className="w-9 h-9 rounded-full shrink-0 bg-primary/10 text-primary font-semibold grid place-items-center text-sm"
                            data-testid="qa-patient-avatar"
                            aria-label={`Initials for ${c.patient_name || "patient"}`}
                        >
                            {(c.patient_name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?"}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h1
                            className="font-display text-base sm:text-lg font-bold leading-tight truncate"
                            data-testid="qa-case-patient-name"
                        >
                            {c.patient_name || "Patient"}
                        </h1>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {[
                                c.patient_age && `${c.patient_age} years`,
                                c.patient_gender && (c.patient_gender.charAt(0).toUpperCase() + c.patient_gender.slice(1)),
                                c.patient_code && `ID: ${c.patient_code}`,
                            ].filter(Boolean).join(", ") || "—"}
                        </div>
                    </div>
                    {/* Doctor + clinic on top-right */}
                    <div className="flex items-center gap-2.5 text-[11px] sm:text-xs shrink-0 flex-wrap" data-testid="qa-case-hero-right">
                        <div
                            className="inline-flex items-center gap-1 text-muted-foreground truncate max-w-[200px]"
                            title={`Provider: Dr. ${(c.doctor_name || "").replace(/^Dr\.\s*/, "")}`}
                        >
                            <Stethoscope size={12} weight="bold" />
                            <span className="truncate font-medium">
                                Dr. {(c.doctor_name || "").replace(/^Dr\.\s*/, "")}
                            </span>
                        </div>
                        {c.practice_location_name && (
                            <>
                                <div className="hidden sm:block h-3 w-px bg-border" />
                                <div
                                    className="inline-flex items-center gap-1 text-muted-foreground truncate max-w-[220px]"
                                    title={c.practice_location_name}
                                >
                                    <Buildings size={12} weight="bold" />
                                    <span className="truncate">{c.practice_location_name}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* iter98.99de — Status pill promoted out of the meta row
                    into its OWN row above (size="lg" capsule) per user
                    direction (universal across all portals). */}
                <div className="mt-2.5 flex items-center">
                    <span data-testid="case-status-pill">
                        <CaseStatusBadge status={c.status} size="lg" />
                    </span>
                </div>

                {/* iter100.75 — Universal informational chip row. */}
                <div className="mt-2">
                    <CaseInfoChipRow caseObj={c} viewerRole="qa" />
                </div>

                {/* Row 2 — meta row */}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px]">
                    <span className="font-mono font-semibold tabular-nums text-foreground/80" data-testid="qa-case-code">
                        {c.code || c.id?.slice(0, 8) || "—"}
                    </span>
                    {(c.revision_count || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase font-bold tracking-wider">
                            Rev {c.revision_count}
                        </span>
                    )}
                    {c.planner_name && (
                        <span className="inline-flex items-center gap-1 text-muted-foreground" data-testid="qa-case-planner">
                            <PencilSimple size={11} weight="bold" /> Planner: <span className="font-semibold text-foreground/80">{c.planner_name}</span>
                        </span>
                    )}
                    {c.diagnosis && (
                        <span className="text-muted-foreground truncate max-w-[280px]" title={c.diagnosis}>
                            {c.diagnosis}
                        </span>
                    )}
                    <RoundTripChip c={c} />
                </div>
            </div>

            {/* Revision context — for QA tracking after doctor pushed back */}
            {c.status === "revision_requested" && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                    <div className="font-semibold text-amber-900">Doctor has requested a revision</div>
                    <div className="text-amber-900/80 text-xs mt-1">
                        Planner is reworking. Once they re-submit, this case will be back in your "Pending review" queue.
                    </div>
                </div>
            )}

            {/* 2-column workspace */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* LEFT (8/12) — clinical + files + plan + timeline */}
                <div className="lg:col-span-2 space-y-5">
                    <Card title="Parties">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <PartyBlock icon={User} title="Patient" name={c.patient_name} sub={c.patient_code} subKind="code" />
                            <PartyBlock icon={Stethoscope} title="Provider" name={c.doctor_name} sub={c.practice_location_name} subKind="text" />
                        </div>
                    </Card>

                    <Card title="Clinical record">
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <KV k="Case type" v={c.clinical?.case_type} />
                            <KV k="Malocclusion" v={c.clinical?.malocclusion_class} />
                            <KV k="Arch" v={c.clinical?.arch || c.arches} />
                            <KV k="Extraction" v={c.clinical?.extraction_plan} />
                            <KV k="Chief complaint" v={c.clinical?.chief_complaint} colspan />
                        </div>
                    </Card>

                    <Card title="Prescription">
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <KV k="IPR" v={c.prescription?.ipr_preference} />
                            <KV k="Attachments" v={c.prescription?.attachments_preference} />
                            <KV k="Elastics" v={c.prescription?.elastics_preference} />
                            <KV k="Esthetic goals" v={c.prescription?.esthetic_goals} />
                            <KV k="Do-not-touch" v={c.prescription?.do_not_touch_teeth} colspan />
                        </div>
                    </Card>

                    {/* Plan panel — shows planner's link/video + structured fields */}
                    <Card title="Treatment plan">
                        <PlanPanel caseId={c.id} plan={c.plan} status={c.status} caseFiles={files} />
                    </Card>

                    {/* Manufacturing spec — QA can confirm or override what
                        the planner set, so production knows which sheets to
                        use. Locked once the case enters production. */}
                    <ManufacturingSpecPanel caseId={c.id} status={c.status} plan={c.plan} allowEdit />

                    {(scans.length > 0 || setupFiles.length > 0 || xrays.length > 0) && (
                        <Card title="Files for review">
                            <div className="space-y-1.5">
                                {scans.map((f) => <FileLink key={f.id} f={f} Icon={Cube} />)}
                                {xrays.map((f) => <FileLink key={f.id} f={f} Icon={ImageIcon} />)}
                                {setupFiles.map((f) => <FileLink key={f.id} f={f} Icon={ClipboardText} className="text-violet-800" />)}
                                {planVideo && <FileLink f={planVideo} Icon={VideoCamera} className="text-violet-800" />}
                                {c.plan?.external_link && (
                                    <a href={c.plan.external_link} target="_blank" rel="noopener noreferrer"
                                       className="text-sm flex items-center gap-2 hover:text-primary text-violet-800"
                                    >
                                        <LinkIcon size={14} /> Plan viewer link
                                    </a>
                                )}
                            </div>
                        </Card>
                    )}

                    {photos.length > 0 && (
                        <Card title={`Photos (${photos.length})`}>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                {photos.map((f) => (
                                    <a key={f.id}
                                        href={`${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="block aspect-square rounded-lg overflow-hidden bg-secondary"
                                    >
                                        <img src={`${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                                    </a>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Planner's submission note */}
                    {(() => {
                        const note = [...(c.timeline || [])].reverse().find((t) => t.action === "submitted_to_qa")?.note;
                        return note ? (
                            <Card title="Planner's submission note">
                                <p className="text-sm whitespace-pre-line">{note}</p>
                            </Card>
                        ) : null;
                    })()}

                    {(c.timeline || []).length > 0 && (
                        <Card title="Activity timeline">
                            <div className="space-y-2">
                                {[...c.timeline].reverse().map((e) => (
                                    <div key={e.id} className="text-xs flex gap-2 items-start">
                                        <span className="text-muted-foreground shrink-0 w-24">
                                            {new Date(e.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                        </span>
                                        <span className="flex-1">
                                            <span className="font-medium">{e.author_name}</span>{" "}
                                            <span className="text-muted-foreground">· {e.action.replace(/_/g, " ")}</span>
                                            {e.note && <div className="text-foreground/80 mt-0.5">{e.note}</div>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                </div>

                {/* RIGHT (4/12) — actions + audit history */}
                <div className="lg:col-span-1 space-y-5 lg:sticky lg:top-20 self-start">
                    <Card title="QA actions">
                        {c.status === "submitted_to_qa" ? (
                            /* Legacy gate path — kept for cases that pre-date the
                               2026-02-28 sampling rollout. Once the queue drains,
                               this whole branch can go. */
                            <div className="space-y-2">
                                <Button onClick={approve} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`qa-approve-${c.id}`}>
                                    <CheckCircle size={14} className="mr-1.5" /> Approve &amp; send to doctor
                                </Button>
                                <Button variant="outline" onClick={() => setRejectOpen(true)} className="w-full text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`qa-reject-open-${c.id}`}>
                                    <ArrowUUpLeft size={14} className="mr-1.5" /> Send back to planner
                                </Button>
                            </div>
                        ) : (
                            /* Sampling-pool path — non-blocking audit. The case
                               is already past QA's gate (the planner sent the plan
                               directly to the doctor); QA records a verdict that
                               feeds planner-quality dashboards but doesn't change
                               the case's status. */
                            <div className="space-y-3">
                                <Button onClick={() => setAuditOpen(true)} disabled={busy} className="w-full" data-testid={`qa-audit-open-${c.id}`}>
                                    <ShieldCheck size={14} className="mr-1.5" weight="fill" /> Record audit decision
                                </Button>
                                <div className="text-[11px] text-muted-foreground bg-secondary/30 rounded-lg p-2 leading-snug">
                                    {c.status === "approved" && "Doctor has approved the plan — your audit is for the planner-quality record."}
                                    {(c.status === "sent_for_approval" || c.status === "ready_for_review") && "Doctor is reviewing the plan — your audit feeds the quality dashboard, doesn't block delivery."}
                                    {c.status === "in_production" && "Case is in production. Late audits are still valuable for trend analysis."}
                                    {c.status === "shipped" && "Case shipped. Audits remain auditable for compliance."}
                                    {c.status === "in_treatment" && "Aligners received at clinic — awaiting fitment. Audits remain auditable for compliance."}
                                    {c.status === "treatment_started" && "Patient is wearing aligners. Audits remain auditable for compliance."}
                                    {c.status === "completed" && "Treatment completed."}
                                    {c.status === "revision_requested" && "Planner is reworking. You can still audit the previous iteration."}
                                    {!["sent_for_approval", "ready_for_review", "approved", "in_production", "shipped", "in_treatment", "treatment_started", "completed", "revision_requested"].includes(c.status) && "—"}
                                </div>
                            </div>
                        )}
                    </Card>

                    <Card title="Audit history">
                        <AuditHistoryPanel caseId={c.id} refreshKey={auditRefreshKey} />
                    </Card>
                </div>
            </div>

            <RejectDialog
                open={rejectOpen}
                caseId={c.id}
                onClose={() => setRejectOpen(false)}
                onDone={() => { setRejectOpen(false); navigate(backLink()); }}
            />

            <AuditDialog
                open={auditOpen}
                caseObj={c}
                onClose={() => setAuditOpen(false)}
                onSubmitted={() => { setAuditRefreshKey((k) => k + 1); }}
            />

            {/* iter98.99i — Premium "Plan sent to doctor for approval"
                confirmation. Mounted at page level so it sits above the
                rest. Both primary and overlay-dismiss route back to the
                QA inbox so the QA reviewer never gets stranded on the
                detail page after the action they just took. */}
            <CaseSubmittedDialog
                open={approvedOpen}
                flow="qa_sent_to_doctor"
                caseCode={c?.code || ""}
                onPrimary={() => { setApprovedOpen(false); navigate(backLink()); }}
            />
        </div>
    );
}

/* ---------- helpers ---------- */
function Card({ title, children }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-5">
            <div className="overline text-muted-foreground mb-3">{title}</div>
            {children}
        </div>
    );
}

function PartyBlock({ icon: Icon, title, name, sub, subKind = "text" }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                <Icon size={18} weight="duotone" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{title}</div>
                <div className="font-semibold truncate">{name || "—"}</div>
                {sub && (
                    subKind === "code" ? (
                        <span className="inline-block mt-1 font-mono text-[10px] text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded">
                            {sub}
                        </span>
                    ) : (
                        <div className="text-xs text-muted-foreground truncate">{sub}</div>
                    )
                )}
            </div>
        </div>
    );
}

function KV({ k, v, colspan }) {
    return (
        <div className={`text-sm flex gap-2 items-baseline ${colspan ? "sm:col-span-2" : ""}`}>
            <div className="text-muted-foreground text-xs min-w-[110px]">{k}</div>
            <div className="flex-1">{v || <span className="text-muted-foreground/60">—</span>}</div>
        </div>
    );
}

function FileLink({ f, Icon, className = "" }) {
    return (
        <a
            href={`${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`}
            target="_blank" rel="noopener noreferrer"
            className={`text-sm flex items-center gap-2 hover:text-primary ${className}`}
        >
            <Icon size={14} /> {f.name}
        </a>
    );
}

function RejectDialog({ open, caseId, onClose, onDone }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) setNote(""); }, [open]);

    const submit = async () => {
        if (!note.trim()) { toast.error("Explain why you're sending it back so the planner can fix it"); return; }
        setBusy(true);
        try {
            await api.put(`/qa/cases/${caseId}/reject`, { note });
            toast.success("Sent back to planner");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reject");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="qa-reject-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowUUpLeft size={18} className="text-rose-600" /> Send back to planner
                    </DialogTitle>
                    <DialogDescription>
                        Explain what needs to change. The planner will see your note alongside the case and re-submit once it's reworked.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-2">
                    <Label htmlFor="qa-reject-note" className="text-xs font-semibold">Rework note</Label>
                    <Textarea
                        id="qa-reject-note"
                        rows={5}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. IPR sequence not staged correctly between aligners 8 and 14, please redo."
                        data-testid="qa-reject-note"
                    />
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="qa-reject-confirm"
                    >
                        {busy ? "Sending…" : "Send back"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
